import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { styled } from "@mui/material";
import * as Yup from "yup";
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import Button from "../../../../customs/Button";
import Input from "../../../../customs/FormInput";
import Select from "../../../../customs/SelectInput";
import CustomTextArea from "../../../../customs/textarea";
import apiCall from "../../../../utils/apiCall";
import { NotificationContext } from "../../../../providers/notification";
import apiCallFormData from "../../../../utils/apiFormDataCall";
import { reOrderFetchedData } from "../../../../hooks/FetchData";


interface CreateScorePayload {
    RouteId: number;
    ScoreTypeId: number;
    MinYears: number;
    MaxYears: number;
    Score: number;
}
interface ScoreTypeData{
    Name: number;
    ScoreTypeId: string;
}
interface ComponentProps {
    handleClose: () => void;
}

interface RouteTypeData{
    RouteId: number;
    RouteName: string;
}
const CreateScoreContent: React.FC<ComponentProps> = ({ handleClose }) => {
    const queryClient = useQueryClient();
    const { showNotification } = useContext(NotificationContext);
    const GetAllScoreTypes = async (): Promise<any> => {
        return await apiCall().get(`/SetUp/GetAllScoreTypes`);
    }


    const GetAllRoute = async (): Promise<any> => {
        return await apiCall().get(`Routes/GetAllRoute`);
    }

    const [getAllRouteQuery, getAllScoreTypeQuery] = useQueries({queries: [
        {queryKey: ['get-allroutes'], queryFn: GetAllRoute, refetchOnWindowFocus: false, retry :1},
        {queryKey: ['get-scoretypes'], queryFn: GetAllScoreTypes,  refetchOnWindowFocus: false, retry :1}
    ]}, );

  const createscore = async (payload: FormData) => {
    return await apiCallFormData().post(
      '/SetUp/CreateCoreHRScore',
      payload
    );
  };

  const createScoreMutation = useMutation(
    ["createscore"],
    createscore,
    { retry: 0 }
  );

  const createScoreHandler = async (
    values: FormikValues,
    resetForm: () => void
  ) => {

    const createScorePayload = new FormData()

    createScorePayload.append('MinYears', values.minYears)
    createScorePayload.append('MaxYears', values.maxYears)
    createScorePayload.append('RouteId', values.routeId)
    createScorePayload.append('Score', values.score)

    try {
      await createScoreMutation.mutateAsync(createScorePayload, {
        onSuccess: () => {    
            showNotification({
                message: 'Score created successful',
                type: 'success',
            });
            queryClient.refetchQueries(['get-AllCoreHrScore']);
            handleClose();
        },
      });
      
    } catch (error: any) {
      showNotification({
        message: error?.response?.data?.Message || error?.message,
        type: "error",
      });
    }
  };

    const validationSchema = Yup.object().shape({
        score: Yup.number().typeError('Only numbers are allowed').required('Score is required'),
        minYears: Yup.number().typeError('Only numbers are allowed').required('Minimum Year is required'),
        maxYears: Yup.number().min(Yup.ref('minYears'), 'Maximum Year Cannot be less than minimum year').typeError('Only numbers are allowed').required('Maximum Year is required'),
        routeId: Yup.string().required('Route is required'),
        
    })
    const formik = useFormik<FormikValues>({
        initialValues: {
            score: '',
            minYears: '',
            maxYears: '',
            routeId: '',
        },
        onSubmit: (values, { resetForm }) => {
            createScoreHandler(values, resetForm);
        },
        validationSchema,
    });

    const RouteTypeOption =
    getAllRouteQuery &&
    getAllRouteQuery?.data?.data?.Data?.length > 0 ? (
        reOrderFetchedData(getAllRouteQuery?.data?.data?.Data).map((route : RouteTypeData ) => (
            <option value={route?.RouteId} key = {route?.RouteId} >
                {route?.RouteName}
            </option>
            ))
        ) : (
        <option disabled>
            {getAllRouteQuery?.isLoading ? 'loading....' : getAllRouteQuery?.error ? 'error': ''}
        </option>
    )
   
    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                <FormWrapper>
                    <Field 
                        name='routeId'
                        type='text'
                        as={Select}
                        label='Route'
                        placeholder='Select'
                        options={RouteTypeOption}
                    />
                    {/* <Field 
                        name='scoreType'
                        type='number'
                        as={Input}
                        label='Score Type'
                        placeholder='Select'
                    /> */}
                    <Field 
                        name='minYears'
                        type='number'
                        as={Input}
                        label='Minimum Years'
                        placeholder='Minimum Years'
                    />
                    <Field 
                        name='maxYears'
                        type='number'
                        as={Input}
                        label='Maximum Years'
                        placeholder='Maximum Years'
                    />
                    <Field 
                        name='score'
                        type='number'
                        as={Input}
                        label='Score'
                        placeholder='Score'
                    />                  
                </FormWrapper>
              
                <ButtonWrapper>
                    <ButtonContainer>
                        <StyledButton text='Cancel' onClick={handleClose}/>
                        <StyledButton 
                            disabled={createScoreMutation.isLoading} 
                            text={createScoreMutation.isLoading ? "Submitting..." : "Submit"}
                            onClick={formik.handleSubmit} 
                            type="submit"
                        />
                    </ButtonContainer>
                </ButtonWrapper>
              
            </form>
        </FormikProvider>
    )
};


export default CreateScoreContent;

const FormWrapper = styled('div')`
    margin-inline: 3.5rem;
    display: grid;
    grid-template-columns: repeat(1fr, 2);
`

const ButtonWrapper = styled('div')`
    display: flex;
    justify-content: flex-end;
    margin-block: 2.5rem;
    padding-inline: 3.5rem;
`

const ButtonContainer = styled('div')`
    display: flex;
    gap: 2rem;
`

const StyledButton = styled(Button)`
    padding-block: 1rem;
    padding-inline: 2rem;
`

